import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Spinner, CheckCircle, Clock, ShieldCheck, Sparkle, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const USE_CASES = ["Auto Dealerships", "Construction Sites", "Real Estate", "Other"];
const TEAM_SIZES = ["1–5 people", "6–15 people", "16–50 people", "51–200 people", "200+ people"];

const EMPTY = { first_name: "", last_name: "", work_email: "", phone: "", company: "", use_case: "", team_size: "", preferred_time: "", message: "", website: "" };

export default function GetDemo() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.work_email || !form.company || !form.use_case || !form.team_size) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/demo-request", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFB] text-zinc-950">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/orbit-icon.jpg" alt="Orbit" className="h-9 w-9 object-contain rounded-md" />
            <span className="font-display font-bold text-sm tracking-tight"><span className="sm:hidden">Orbit</span><span className="hidden sm:inline">Orbit Inventory Management</span></span>
          </Link>
          <Link to="/"><Button variant="ghost" className="h-10 font-semibold gap-2" data-testid="demo-back-home"><ArrowLeft size={16} weight="bold" /> Back</Button></Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-12 lg:py-20 grid lg:grid-cols-2 gap-12 items-start">
        <div className="lg:sticky lg:top-24">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 mb-3">Book a Demo</p>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">See Orbit in action</h1>
          <p className="mt-4 text-lg text-zinc-600 leading-relaxed max-w-md">
            Tell us about your business and we'll walk you through exactly how Orbit fits your workflow — live, no pressure.
          </p>
          <div className="mt-8 space-y-4">
            {[
              { icon: Clock, title: "15-Minute Demo", desc: "A focused walkthrough of the features that matter most to your business." },
              { icon: ShieldCheck, title: "No Commitment", desc: "See if it's the right fit before you decide anything." },
              { icon: Sparkle, title: "Tailored to You", desc: "We customise every demo based on your industry and team size." },
            ].map((b) => (
              <div key={b.title} className="flex gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><b.icon size={20} weight="fill" className="text-emerald-600" /></div>
                <div>
                  <p className="font-semibold">{b.title}</p>
                  <p className="text-sm text-zinc-500">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-zinc-400">Your information is kept private and never shared with third parties.</p>
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8 shadow-xl shadow-zinc-200/50" data-testid="demo-form-card">
          {done ? (
            <div className="py-10 text-center" data-testid="demo-confirmation">
              <CheckCircle size={56} weight="fill" className="text-emerald-500 mx-auto" />
              <h2 className="font-display text-2xl font-bold mt-4">Request received!</h2>
              <p className="text-zinc-600 mt-2 max-w-sm mx-auto">Thanks, {form.first_name || "there"}. Our team will reach out to {form.work_email} shortly to schedule your demo.</p>
              <Link to="/"><Button className="mt-6 h-12 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="demo-done-home">Back to Home</Button></Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {/* Honeypot — hidden from users, bots tend to fill it */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
                <label>Website<input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="demo-website-hp" /></label>
              </div>
              <h2 className="font-display text-xl font-bold">About your business</h2>
              <p className="text-sm text-zinc-500 -mt-2">We'll use this to personalise your demo.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">First Name *</Label>
                  <Input value={form.first_name} onChange={set("first_name")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="demo-first-name" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Last Name *</Label>
                  <Input value={form.last_name} onChange={set("last_name")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="demo-last-name" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Work Email *</Label>
                <Input type="email" value={form.work_email} onChange={set("work_email")} placeholder="you@company.com" className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="demo-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">Phone <span className="font-normal text-zinc-400">(optional)</span></Label>
                  <Input value={form.phone} onChange={set("phone")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="demo-phone" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Company Name *</Label>
                  <Input value={form.company} onChange={set("company")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="demo-company" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Primary Use Case *</Label>
                <Select value={form.use_case} onValueChange={(v) => setForm((f) => ({ ...f, use_case: v }))}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid="demo-use-case"><SelectValue placeholder="Select your use case…" /></SelectTrigger>
                  <SelectContent>{USE_CASES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold">Team Size *</Label>
                <Select value={form.team_size} onValueChange={(v) => setForm((f) => ({ ...f, team_size: v }))}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid="demo-team-size"><SelectValue placeholder="Select team size…" /></SelectTrigger>
                  <SelectContent>{TEAM_SIZES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold">Preferred Date & Time <span className="font-normal text-zinc-400">(optional)</span></Label>
                <Input type="datetime-local" value={form.preferred_time} onChange={set("preferred_time")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="demo-preferred-time" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Anything else? <span className="font-normal text-zinc-400">(optional)</span></Label>
                <Textarea value={form.message} onChange={set("message")} rows={3} placeholder="Tell us what you'd like to see…" className="mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="demo-message" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold gap-2" data-testid="demo-submit">
                {loading ? <Spinner size={18} className="animate-spin" /> : <>Request My Demo <ArrowRight size={18} weight="bold" /></>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
